import LogoutIcon from "@mui/icons-material/Logout"
import MenuIcon from "@mui/icons-material/Menu"
import MenuOpenIcon from "@mui/icons-material/MenuOpen"
import PermDeviceInformationIcon from "@mui/icons-material/PermDeviceInformation"
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined"
import { Box, Stack, ButtonBase, Avatar, Badge } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { ReactElement, useEffect, useState } from "react"
import { useNavigate } from "react-router-dom"

import SearchInput from "./searchInput"
import { APPBAR_HEIGHT_PX } from "../../constants/generalConstants"
import {
    TT_SETTINGS,
    TT_SIGN_IN,
    TT_SIGN_OUT,
    TT_TOTP,
} from "../../constants/tooltips"
import { useAppDispatch } from "../../hooks/useDispatch"
import { useGetState } from "../../hooks/useGetState"
import { useGetUserData } from "../../hooks/useGetUserData"
import { useScreenSize } from "../../hooks/useScreenSize"
import {
    getNavBarExpand,
    getSideBar,
    setNavBarExpand,
    setShowPreferences,
    setShowMFA,
    setShowAccountSetUp,
    getShowAccountSetUp,
} from "../../redux/preferencesSlice"
import { CLOSED, OPEN, SUPPRESSED } from "../../redux/types"
import { LOGIN } from "../../router/routes"
import {
    GRID_SPACING,
    SEARCH_BAR_ITEM_RADIUS_MULTIPLIER,
} from "../../themes/defaultThemeParams"
import AvatarIconButton from "../buttons/avatarIconButton"
import AppTooltip from "../helper/appTooltip"
import ConnectDirectLogo from "../logos/logos"
import SearchPanel from "../searchPanel/searchPanel"

/**
 * The top banner on all webpages.
 *
 * @example
 * <AppBar>
 */
const AppBar = (): ReactElement => {
    const theme = useTheme()
    const isDarkMode = theme.palette.mode === "dark"
    const dispatch = useAppDispatch()
    const sideBar = useGetState(getSideBar)
    const navbarExpand = useGetState(getNavBarExpand)
    const accountDialogState = useGetState(getShowAccountSetUp)
    const navigate = useNavigate()
    const { isTabletOrMob } = useScreenSize()
    const [open, setOpen] = useState(false)
    const [search, setSearch] = useState("")

    const {
        onSignOut,
        isSignedIn,
        isSetUpPending,
        firstName,
        lastName,
        isMfaRequired,
        showUnapprovedWarning,
        isConfiguring,
    } = useGetUserData()

    useEffect(() => {
        if (
            isSetUpPending &&
            accountDialogState !== SUPPRESSED &&
            isSignedIn &&
            !isConfiguring
        ) {
            dispatch(setShowAccountSetUp(OPEN))
        } else if (isMfaRequired && isSignedIn) {
            dispatch(setShowMFA(true))
        } else {
            dispatch(setShowMFA(false))
            if (accountDialogState !== SUPPRESSED) {
                dispatch(setShowAccountSetUp(CLOSED))
            }
        }
    }, [
        isMfaRequired,
        showUnapprovedWarning,
        dispatch,
        isSignedIn,
        isSetUpPending,
        isConfiguring,
        accountDialogState,
    ])

    const name = isSignedIn ? `${firstName} ${lastName}` : "Sign Up / Log In"

    return (
        <>
            <SearchPanel
                open={open}
                setOpen={setOpen}
                search={search}
                setSearch={setSearch}
            />
            <Box
                height={APPBAR_HEIGHT_PX}
                width="100%"
                padding={isTabletOrMob ? 1 : 3}
                sx={{
                    backgroundColor: "background.frameBackground",
                    backgroundImage: "none",
                }}
                display="flex"
                flexDirection="row"
            >
                {isTabletOrMob ? (
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="flex-start"
                        spacing={GRID_SPACING}
                        flexGrow={1}
                    >
                        <ConnectDirectLogo fontSize="80px" />

                        <SearchInput
                            value={search}
                            onClick={() => setOpen(true)}
                        />
                    </Stack>
                ) : (
                    <>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="space-between"
                            spacing={0}
                            width={sideBar ? "250px" : undefined}
                            flexGrow={sideBar ? undefined : 1}
                        >
                            <ConnectDirectLogo fontSize="90px" />

                            {sideBar && (
                                <AvatarIconButton
                                    onClick={() =>
                                        dispatch(setNavBarExpand(!navbarExpand))
                                    }
                                    Icon={
                                        navbarExpand ? MenuOpenIcon : MenuIcon
                                    }
                                />
                            )}
                        </Stack>
                        <Stack
                            direction="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            spacing={GRID_SPACING}
                            flexGrow={20}
                        >
                            <SearchInput
                                value={search}
                                onClick={() => setOpen(true)}
                            />
                            <Stack
                                direction="row"
                                alignItems="center"
                                justifyContent="flex-end"
                                spacing={GRID_SPACING}
                            >
                                {isSignedIn && (
                                    <>
                                        {isMfaRequired && (
                                            <Badge
                                                color="error"
                                                variant="dot"
                                                overlap="circular"
                                            >
                                                <AvatarIconButton
                                                    Icon={
                                                        PermDeviceInformationIcon
                                                    }
                                                    onClick={() =>
                                                        dispatch(
                                                            setShowMFA(true)
                                                        )
                                                    }
                                                    tooltip={TT_TOTP}
                                                />
                                            </Badge>
                                        )}
                                        <AvatarIconButton
                                            onClick={onSignOut}
                                            Icon={LogoutIcon}
                                            color="primary"
                                            tooltip={TT_SIGN_OUT}
                                        />
                                    </>
                                )}
                                <AvatarIconButton
                                    onClick={() =>
                                        dispatch(setShowPreferences(true))
                                    }
                                    Icon={SettingsOutlinedIcon}
                                    tooltip={TT_SETTINGS}
                                />
                                <AppTooltip
                                    title={isSignedIn ? "" : TT_SIGN_IN}
                                >
                                    <ButtonBase
                                        sx={{
                                            backgroundColor: isDarkMode
                                                ? theme.palette.primary.dark
                                                : theme.palette.primary.light,
                                            color: isDarkMode
                                                ? theme.palette.primary.light
                                                : theme.palette.primary.dark,
                                            "&:hover": {
                                                backgroundColor: !isDarkMode
                                                    ? theme.palette.primary.dark
                                                    : theme.palette.primary
                                                          .light,
                                                color: !isDarkMode
                                                    ? theme.palette.primary
                                                          .light
                                                    : theme.palette.primary
                                                          .dark,
                                                transition:
                                                    "all .2s ease-in-out",
                                                ".appBarAvatar": {
                                                    color: theme.palette.primary
                                                        .light,
                                                    transition: "inherit",
                                                },
                                            },

                                            ...theme.typography.body1,
                                            fontWeight: "bold",
                                            height: "50px",
                                            paddingX: 4,
                                            borderRadius:
                                                SEARCH_BAR_ITEM_RADIUS_MULTIPLIER,
                                        }}
                                        onClick={() => navigate(LOGIN)}
                                    >
                                        {name}
                                        <Avatar
                                            variant="rounded"
                                            className="appBarAvatar"
                                            sx={{
                                                borderRadius: "14px",
                                                fontSize: "1.5rem",
                                                background: "#00000000",
                                                color: !isDarkMode
                                                    ? theme.palette.primary.dark
                                                    : theme.palette.primary
                                                          .light,
                                            }}
                                            color="inherit"
                                        />
                                    </ButtonBase>
                                </AppTooltip>
                            </Stack>
                        </Stack>
                    </>
                )}
            </Box>
        </>
    )
}

export default AppBar
